/* eslint-disable no-restricted-syntax */
import { getValidAccessToken } from 'services/auth';
import { DynamicStructuredTool } from '@langchain/core/tools';
import { AIMessage, BaseMessage, HumanMessage, RemoveMessage, isAIMessage } from '@langchain/core/messages';
import { createAgent, createMiddleware, summarizationMiddleware } from 'langchain';
import { MemorySaver } from '@langchain/langgraph';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { ToolFunction } from './types';
import { getMcpTools } from './getMcpTools';
import { freezeAndSortTools } from './freezeAndSortTools';
import { createLocalTools } from 'views/inference/utils/localTools';
import type { ActiveOrder } from 'views/inference/utils/screenReader';
import { ORCHESTRATOR_ROUTING_PREFIX } from './orchestratorRouting';
import type { ChatOpenAI } from '@langchain/openai';
import type { GQLPrompt } from 'graphql/questions-and-answers/queries';
import { getDocumentOcr } from './ocrService';
import { createStreamingChatOpenAI } from './streamingChatModel';
import {
  ModelCallTelemetryHandler,
  NO_RESPONSE_NOTICE,
  STUCK_CONVERSATION_NOTICE,
  TelemetrySource,
  TurnDecision,
  classifyModelTurn,
  contentToText,
  isRolelessMessage,
  readFinishReason,
  sanitizeHistory,
  toAssistantTurn,
} from './modelTurnGuard';

const normalizeOcrText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'object' || value === null) {
    throw new Error('OCR response did not contain text blocks');
  }

  const response = value as Record<string, unknown>;
  if (response.data !== undefined) {
    return normalizeOcrText(response.data);
  }

  if (response.getDocumentText !== undefined) {
    return normalizeOcrText(response.getDocumentText);
  }

  if (typeof response.text === 'string') {
    return response.text;
  }

  if (Array.isArray(response.Blocks)) {
    const lines = response.Blocks
      .filter((block): block is Record<string, unknown> => (
        typeof block === 'object' && block !== null
      ))
      .filter((block) => block.BlockType === 'LINE' && typeof block.Text === 'string')
      .map((block) => block.Text as string);

    if (lines.length > 0) {
      return lines.join('\n');
    }
  }

  throw new Error('OCR response did not contain text blocks');
};

const EXTRACTION_TEMPERATURE = 0.1;
const EXTRACTION_MAX_TOKENS = 16000;
const ORCHESTRATOR_RECURSION_LIMIT = 133;
const TURN_DECISION_EVENT = 'turn_decision';
const REJECTED_REQUEST_STATUSES = [400, 422];
const PENDING_TOKEN = '';

const normalizeLanguage = (lang: string): string => {
  if (lang.startsWith('es')) return 'spanish';
  if (lang.startsWith('en')) return 'english';
  return 'english';
};

type PendingModelEmission = {
	toolCalls: Array<{ id: string, name: string, args: string, status: 'running' }>,
	thought?: string,
};

export type SubAgentCallUpdate = {
	readonly subagentName: string;
	readonly status: 'started' | 'thinking' | 'tool_call' | 'completed' | 'error';
	readonly message?: string;
};

export class LlmProxyService {
  private model: ChatOpenAI;

  private agent: ReturnType<typeof createAgent>;

  private checkpointer: MemorySaver;

  private config: { configurable: { thread_id: string } };

  private activeOrder: ActiveOrder = null;

  private currentOrderNarrative: string;

  private currentTools: ToolFunction[];

  private localTools: DynamicStructuredTool[];

  /** Frozen, alphabetically-sorted base tools (MCP + local). Set after loadMcpTools() resolves. */
  private _initializedBaseTools: readonly DynamicStructuredTool[] | null = null;

  /** Subagent wrapper tools created at initialization. Reused across clearHistory resets. */
  private _subagentWrappers: DynamicStructuredTool[] = [];

  /** Side-channel for forwarding subagent updates during active invoke(). Cleared in finally block. */
  private _currentInvokeCallbacks: Set<(update: SubAgentCallUpdate) => void> = new Set();

  /** True once a message has been sent in the current thread; reset when the thread is cleared. */
  private _hasConversation = false;

  /** Save-guard decision of the model call in the current invoke(). Set by the turn guard middleware. */
  private _turnDecision: TurnDecision | null = null;

  private _consecutiveFailures = 0;

  private consumeTurnDecision = (): TurnDecision | null => {
    const decision = this._turnDecision;
    this._turnDecision = null;
    return decision;
  };

  /** Flag to defer clearHistory() until active invoke() completes. */
  private _clearPending = false;

  /** Invocation-scoped attachment envelope. Set after AGENT_BUSY guard, cleared in finally. */
  private _activeAttachmentEnvelope: {
		images: Array<{ label: string; baseUrl: string }>;
		ocrEntries: Array<{ label: string; text: string | null }>;
	} | null = null;

  /** GQL prompts for subagent wrappers. Set via setPrompts(). */
  private _gqlPrompts: GQLPrompt[] = [];

  private systemPrompt: string;

  private userInfo: string | null;

  private temperature: number = 0.9;

  private modelNameSelected: string;

  private modelAgentName: string;

  private conversationLanguage: string;

  private selectedPrompt: GQLPrompt | null = null;

  constructor(systemPrompt: string, modelAgent: string, language?: string) {
    this.modelNameSelected = modelAgent;
    this.modelAgentName = modelAgent;
    this.currentOrderNarrative = '';
    this.currentTools = [];
    this.userInfo = null;
    this.conversationLanguage = normalizeLanguage(language || 'en');
    this.systemPrompt = systemPrompt;
    this.localTools = createLocalTools({ getActiveOrder: () => this.activeOrder });
    this.checkpointer = new MemorySaver();
    this.config = { configurable: { thread_id: `session-${uuidv4()}` } };
    // Structure-only placeholders: invoke() rebuilds the agent with a fresh token before every turn.
    this.model = this.buildModel(PENDING_TOKEN, modelAgent);
    this.agent = this.buildAgent(this.model, this.systemPrompt);
  }

  private appendLanguageInstruction = (prompt: string): string => {
    return `${prompt}\n\nRespond in ${this.conversationLanguage}`;
  };

  public setPrompt = (prompt: string): void => {
    this.systemPrompt = prompt;
  };

  public setPrompts = (prompts: GQLPrompt[]): void => {
    this._gqlPrompts = prompts;
    this.selectedPrompt = prompts.length === 1 ? prompts[0] : null;
    this._createSubagentWrappers();
    this.rebuildAgent();
  };

  public setLanguage = (language: string): void => {
    this.conversationLanguage = normalizeLanguage(language);
    this.rebuildAgent();
  };

  public loadMcpTools = async (): Promise<void> => {
    try {
      const mcpTools = this._withoutLocalNameCollisions(await getMcpTools());
      this._initializedBaseTools = freezeAndSortTools([...mcpTools, ...this.localTools]);
      this._createSubagentWrappers();
      this.rebuildAgent();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[LlmProxyService] Failed to load MCP tools:', error);
      this._initializedBaseTools = freezeAndSortTools([...this.localTools]);
      this._createSubagentWrappers();
      this.rebuildAgent();
    }
  };

  // Tool names must be unique for the agent; a browser-local tool (e.g. readScreen) wins over an MCP tool with its name.
  private _withoutLocalNameCollisions = (mcpTools: DynamicStructuredTool[]): DynamicStructuredTool[] => {
    const localNames = new Set(this.localTools.map((tool) => tool.name));
    const collisions = mcpTools.filter((tool) => localNames.has(tool.name));
    if (collisions.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[LlmProxyService] Ignoring MCP tools that collide with local tools:', collisions.map((tool) => tool.name));
    }
    return mcpTools.filter((tool) => !localNames.has(tool.name));
  };

  private _createSubagentWrappers = (): void => {
    this._subagentWrappers = this._gqlPrompts.map((prompt) => {
      const subagentName = prompt.name;
      const promptContent = prompt.english.content;
      const promptDescription = prompt.english.description;

      return new DynamicStructuredTool({
        name: `delegate_${prompt._id}`,
        description: promptDescription,
        schema: z.object({ query: z.string() }),
        func: async (args: { query: string }) => {
          const callbacks = Array.from(this._currentInvokeCallbacks);
          callbacks.forEach((cb) => cb({ subagentName, status: 'started' }));

          try {
            const subagentToken = await getValidAccessToken();
            const subagentModel = this.buildModel(subagentToken, prompt.model, undefined, undefined, 'subagent');
            const subagent = this.buildSubAgent(subagentModel, promptContent);

            let finalText = '';
            let lastContentfulAiMessage: any = null;
            const seenMessageIds = new Set<string>();

            const envelope = this._activeAttachmentEnvelope;
            let subagentInput: string | Array<{ type: string; text?: string; image_url?: { url: string } }>;

            if (envelope && (envelope.images.length > 0 || envelope.ocrEntries.length > 0)) {
              const contentParts: Array<{ type: string; text?: string; image_url?: { url: string } }> = [];

              contentParts.push({ type: 'text', text: args.query });

              envelope.ocrEntries.forEach((entry) => {
                if (entry.text !== null) {
                  contentParts.push({ type: 'text', text: `[${entry.label} - OCR extracted text from document]\n${entry.text}` });
                } else {
                  contentParts.push({ type: 'text', text: `[${entry.label} - OCR unavailable]` });
                }
              });

              envelope.images.forEach((img) => {
                contentParts.push({ type: 'text', text: `[${img.label}]` });
                contentParts.push({ type: 'image_url', image_url: { url: img.baseUrl } });
              });

              subagentInput = contentParts;
            } else {
              subagentInput = args.query;
            }

            const stream = await subagent.stream(
              { messages: [new HumanMessage(subagentInput)] },
              { configurable: { thread_id: `subagent-${uuidv4()}` }, recursionLimit: 50, streamMode: ["updates", "messages"] }
            );

            const processMsgContent = (content: any): string => {
              if (typeof content === 'string') return content;
              if (Array.isArray(content)) {
                return content
                  .filter((b: any) => b.type === 'text')
                  .map((b: any) => b.text)
                  .join(' ');
              }
              return '';
            };

            for await (const event of stream) {
              const isTuple = Array.isArray(event) && typeof event[0] === 'string';
              const mode = isTuple ? (event[0] as string) : undefined;
              const payload = isTuple ? (event[1] as any) : event;

              if (mode === 'messages') {
                const [messageChunk] = payload as [any, any];
                if (messageChunk && messageChunk._getType?.() === 'ai' && !messageChunk.tool_call_chunks?.length) {
                  const text = processMsgContent(messageChunk.content);
                  if (text) {
                    finalText += text;
                    callbacks.forEach((cb) => cb({ subagentName, status: 'thinking' }));
                  }
                }
              } else {
                const nodeUpdate = Object.values(payload)[0] as any;
                if (nodeUpdate?.messages) {
                  for (const msg of nodeUpdate.messages) {
                    const msgId = msg.id || msg.tool_call_id;
                    if (msgId && seenMessageIds.has(msgId)) {
                      // eslint-disable-next-line no-continue
                      continue;
                    }
                    if (msgId) seenMessageIds.add(msgId);

                    const type = msg._getType();
                    if (type === 'ai') {
                      const text = processMsgContent(msg.content);
                      if (text.trim()) {
                        lastContentfulAiMessage = msg;
                        finalText = text;
                      }
                    }
                  }
                }
              }
            }

            const output = lastContentfulAiMessage ? processMsgContent(lastContentfulAiMessage.content) : finalText || '';
            callbacks.forEach((cb) => cb({ subagentName, status: 'completed', message: output }));
            return output;
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            callbacks.forEach((cb) => cb({ subagentName, status: 'error', message: errorMsg }));
            throw error;
          }
        }
      });
    });
  };

  private buildModel = (
    token: string,
    modelName: string,
    temperature: number = this.temperature,
    maxTokens?: number,
    telemetrySource: TelemetrySource = 'orchestrator'
  ): ChatOpenAI => {
    const model = createStreamingChatOpenAI({
      configuration: {
        baseURL: import.meta.env.VITE_LLM_PROXY,
      },
      modelName,
      apiKey: token,
      temperature,
      ...(maxTokens === undefined ? {} : { maxTokens }),
      streaming: true,
      maxRetries: 0,
      callbacks: [new ModelCallTelemetryHandler(telemetrySource)],
    });
    return model;
  };

  private createTurnGuard = () => createMiddleware({
    name: 'turnGuard',
    wrapModelCall: (request, handler) => {
      const messages = sanitizeHistory(request.messages);
      const changed = messages.length !== request.messages.length || messages.some((m, i) => m !== request.messages[i]);
      return handler(changed ? { ...request, messages } : request);
    },
    afterModel: {
      // A discarded turn must end the run: without the jump, the router would pick up
      // unanswered tool calls from an earlier assistant turn and execute them.
      canJumpTo: ['end'],
      hook: (state, runtime) => {
        const last = state.messages.at(-1);
        if (!last) return undefined;

        let turn: AIMessage | null = null;
        if (isAIMessage(last)) turn = last;
        else if (isRolelessMessage(last)) turn = toAssistantTurn(last);
        if (!turn) return undefined;

        const decision = classifyModelTurn(turn, readFinishReason(last.response_metadata));
        this._turnDecision = decision;
        runtime?.writer?.({ type: TURN_DECISION_EVENT, persisted: decision.persisted });
        if (decision.persisted) return undefined;
        if (!last.id) return { jumpTo: 'end' };

        return { messages: [new RemoveMessage({ id: last.id })], jumpTo: 'end' };
      },
    },
  });

  private buildAgent = (model: ChatOpenAI, prompt: string): ReturnType<typeof createAgent> => {
    const allTools = this._initializedBaseTools ? [...this._initializedBaseTools] : [...this.localTools];
    const toolsWithWrappers = [...allTools, ...this._subagentWrappers];
    return createAgent({
      model,
      tools: toolsWithWrappers,
      systemPrompt: this.appendLanguageInstruction(`${ORCHESTRATOR_ROUTING_PREFIX}\n\n${prompt}`),
      checkpointer: this.checkpointer,
      middleware: [
        this.createTurnGuard(),
        summarizationMiddleware({
          model,
          trigger: { tokens: 40000, messages: 20 },
          keep: { messages: 15 }
        })
      ]
    });
  };

  private buildSubAgent = (model: ChatOpenAI, prompt: string): ReturnType<typeof createAgent> => {
    const allTools = this._initializedBaseTools ? [...this._initializedBaseTools] : [...this.localTools];
    return createAgent({
      model,
      tools: allTools,
      systemPrompt: prompt,
      checkpointer: undefined
    });
  };

  private rebuildAgent = (token: string = PENDING_TOKEN): void => {
    if (this.selectedPrompt) {
      const selectedModel = this.buildModel(token, this.selectedPrompt.model);
      const selectedPromptContent = this.selectedPrompt.english.content;
      this.agent = this.buildAgent(selectedModel, selectedPromptContent);
    } else {
      this.model = this.buildModel(token, this.modelNameSelected);
      this.agent = this.buildAgent(this.model, this.systemPrompt);
    }
  };

  public invoke = async (
    question: string,
    onMessageResponse: (message: string) => void,
    options?: {
			images?: { baseUrl: string, sendAs: "Text" | "Image", label?: string }[],
			onThought?: (thought: string) => void,
			onToolCall?: (toolCalls: { id: string, name: string, args: string, result?: string, status: 'running' | 'completed' | 'error' }[]) => void,
			onMessageDelta?: (delta: string) => void,
			onStreamStart?: () => void,
			onSubAgentUpdate?: (update: SubAgentCallUpdate) => void,
			onAttachmentError?: (error: string) => void,
			onMessageIncomplete?: (notice: string) => void,
		}
  ) => {
    if (this._currentInvokeCallbacks.size > 0) {
      throw new Error('AGENT_BUSY: an invocation is already in progress');
    }

    let llmResponse = '';
    let accumulatedStreamedText = '';
    this._hasConversation = true;
    this._turnDecision = null;

    const reportIncomplete = (notice: string): void => {
      if (!accumulatedStreamedText.trim()) {
        onMessageResponse(notice);
        return;
      }
      if (options?.onMessageIncomplete) {
        options.onMessageIncomplete(notice);
        return;
      }
      onMessageResponse(`${accumulatedStreamedText}\n\n${notice}`);
    };

    try {
      if (options?.onSubAgentUpdate) {
        this._currentInvokeCallbacks.add(options.onSubAgentUpdate);
      }

      const onThought = options?.onThought;
      const onToolCall = options?.onToolCall;
      const onMessageDelta = options?.onMessageDelta;
      const onStreamStart = options?.onStreamStart;
      const onAttachmentError = options?.onAttachmentError;

      const requestThreadId = this.config.configurable.thread_id;

      const messageList: BaseMessage[] = [];

      const messageContent: Array<{ type: string; text?: string; image_url?: { url: string } }> = [];

      if (options?.images && options.images.length > 0) {
        const imageAttachments = options.images.filter(({ sendAs }) => sendAs === "Image");
        const textAttachments = options.images.filter(({ sendAs }) => sendAs === "Text");

        const ocrResults: Array<{ label: string; text: string | null; error?: string }> = [];
        if (textAttachments.length > 0) {
          const ocrPromises = textAttachments.map(async (img, idx) => {
            try {
              const ocrText = normalizeOcrText(await getDocumentOcr(img.baseUrl));
              return { label: img.label || `Page ${idx + 1}`, text: ocrText, error: undefined };
            } catch (error) {
              const errorMsg = error instanceof Error ? error.message : 'Unknown OCR error';
              if (onAttachmentError) {
                onAttachmentError(`OCR failed for ${img.label || `Page ${idx + 1}`}: ${errorMsg}`);
              }
              return { label: img.label || `Page ${idx + 1}`, text: null, error: errorMsg };
            }
          });

          const results = await Promise.all(ocrPromises);
          ocrResults.push(...results);
        }

        this._activeAttachmentEnvelope = {
          images: imageAttachments.map((img) => ({ label: img.label || 'Image', baseUrl: img.baseUrl })),
          ocrEntries: ocrResults.map((r) => ({ label: r.label, text: r.text })),
        };

        let textContent = question || 'Processing attached images...';
        if (ocrResults.length > 0) {
          const successfulOcr = ocrResults.filter((r) => r.text !== null);
          if (successfulOcr.length > 0) {
            const ocrText = successfulOcr
              .map((r) => `[${r.label} - OCR extracted text from document]\n${r.text}`)
              .join('\n\n');
            textContent = question ? `${question}\n\n${ocrText}` : ocrText;
          }
        }
        messageContent.push({ type: 'text', text: textContent });

        imageAttachments.forEach((img) => {
          messageContent.push({
            type: 'text',
            text: `[${img.label || 'Image'}]`
          });
          messageContent.push({
            type: 'image_url',
            image_url: { url: img.baseUrl }
          });
        });
      } else {
        messageContent.push({ type: 'text', text: question });
      }

      messageList.push(new HumanMessage(messageContent));

      try {
        let toolCallEmitted = false;
        const streamedMessageIds = new Set<string>();
        const seenMessageIds = new Set<string>();
        let pendingModelEmission: PendingModelEmission | null = null;
        const flushPendingModelEmission = (): void => {
          const emission = pendingModelEmission;
          pendingModelEmission = null;
          if (!emission) return;
          if (emission.toolCalls.length > 0 && onToolCall) {
            onToolCall(emission.toolCalls);
            toolCallEmitted = true;
          }
          if (emission.thought && onThought) onThought(emission.thought);
        };

        const processMsgContent = (content: any): string => {
          if (typeof content === 'string') return content;
          if (Array.isArray(content)) {
            return content
              .filter((b: any) => b.type === 'text')
              .map((b: any) => b.text)
              .join(' ');
          }
          return '';
        };

        const getMessageText = (msg: any): string => {
          if (typeof msg.content === 'string') return msg.content;
          if (Array.isArray(msg.content)) {
            return msg.content
              .filter((b: any) => b.type === 'text')
              .map((b: any) => b.text)
              .join(' ');
          }
          return '';
        };

        // Rebuild with the token current at call time so a rotation is never served stale.
        this.rebuildAgent(await getValidAccessToken());
        const requestAgent = this.agent;

        // Stream agent to get real-time events and tool execution
        const stream = await requestAgent.stream(
          { messages: messageList },
          {
            configurable: { thread_id: requestThreadId },
            recursionLimit: ORCHESTRATOR_RECURSION_LIMIT,
            streamMode: ['updates', 'messages', 'custom']
          }
        );

        // Process stream events as they arrive
        for await (const event of stream) {
          const isTuple = Array.isArray(event) && typeof event[0] === 'string';
          const mode = isTuple ? (event[0] as string) : undefined;
          const payload = isTuple ? (event[1] as any) : event;

          // Handle 'messages' mode: text chunks from streaming
          if (mode === 'messages') {
            const [messageChunk] = payload as [any, any];
            // Accept both 'ai' and 'generic' types for text chunks (LangChain version compatibility)
            const msgType = messageChunk?._getType?.();
            const isAiChunk = msgType === 'ai' || msgType === 'generic';
            if (messageChunk && isAiChunk && !messageChunk.tool_call_chunks?.length) {
              const text = processMsgContent(messageChunk.content);
              if (text) {
                const prefix = toolCallEmitted && accumulatedStreamedText ? '\n\n' : '';
                toolCallEmitted = false;
                accumulatedStreamedText += prefix + text;
                if (messageChunk.id) streamedMessageIds.add(messageChunk.id);
                if (onMessageDelta) {
                  onMessageDelta(prefix + text);
                }
              }
            }
          } else if (mode === 'custom') {
            if (payload?.type === TURN_DECISION_EVENT) {
              if (payload.persisted) flushPendingModelEmission();
              else pendingModelEmission = null;
            }
          } else {
            // Handle 'updates' mode: node updates with complete messages
            const nodeUpdate = Object.values(payload)[0] as any;
            if (nodeUpdate?.messages) {
              if (nodeUpdate.messages.some((msg: any) => msg._getType?.() === 'tool')) flushPendingModelEmission();
              for (const msg of nodeUpdate.messages) {
                const msgId = msg.id || msg.tool_call_id;
                if (msgId && seenMessageIds.has(msgId)) {
                  // eslint-disable-next-line no-continue
                  continue;
                }
                if (msgId) seenMessageIds.add(msgId);

                const type = msg._getType?.();

                // Handle both 'ai' and 'generic' types for assistant output
                // Generic messages can occur when Bedrock omits role in streaming deltas
                const isAssistantMessage = type === 'ai' || (type === 'generic' && !msg.tool_call_id && !msg.name);
                if (isAssistantMessage) {
                  const text = processMsgContent(msg.content);
                  const isAlreadyStreamed = msg.id && streamedMessageIds.has(msg.id);
                  if (text.trim() && !isAlreadyStreamed) {
                    accumulatedStreamedText += text;
                    if (onMessageDelta) {
                      onMessageDelta(text);
                    }
                  }

                  pendingModelEmission = {
                    toolCalls: (msg.tool_calls ?? []).map((tc: any) => ({
                      id: tc.id,
                      name: tc.name,
                      args: JSON.stringify(tc.args, Object.keys(tc.args).sort()),
                      status: 'running' as const
                    })),
                    thought: msg.content && text.trim() ? text : undefined,
                  };
                }

                if (type === 'tool') {
                  const content = processMsgContent(msg.content);
                  if (onToolCall) {
                    onToolCall([{
                      id: msg.tool_call_id,
                      name: msg.name || 'tool',
                      args: '',
                      result: content,
                      status: 'completed',
                    }]);
                  }
                }
              }
            }
          }
        }

        const decision = this.consumeTurnDecision();
        if (!decision || decision.persisted) flushPendingModelEmission();
        this._consecutiveFailures = 0;

        if (onStreamStart) {
          onStreamStart();
        }

        // Log OpenAI-compatible proxy cache usage diagnostics when available
        const { usage } = stream as any;
        if (usage) {
          const cachedTokens = usage.prompt_tokens_details?.cached_tokens;
          const cacheWriteTokens = usage.cache_write_tokens;
          if (cachedTokens !== undefined || cacheWriteTokens !== undefined) {
            // eslint-disable-next-line no-console
            console.log('[LlmProxyService] Cache diagnostics:', {
              cached_tokens: cachedTokens,
              cache_write_tokens: cacheWriteTokens,
              thread_id: requestThreadId,
            });
          }
        }

        if (decision && !decision.persisted && decision.notice) {
          reportIncomplete(decision.notice);
          return decision.notice;
        }

        const finalOutput = accumulatedStreamedText;

        if (!finalOutput) {
          onMessageResponse(NO_RESPONSE_NOTICE);
          return NO_RESPONSE_NOTICE;
        }

        llmResponse = finalOutput;
        onMessageResponse(llmResponse);
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        let errorMessage = 'Unexpected error occurred.';
        if (message) {
          const index = message.indexOf("Troubleshooting URL");
          errorMessage = (index !== -1 ? message.substring(0, index) : message).trim();
        }

        // The thread is kept on error: the checkpoint is not reset here. Clearing it is a user decision.
        this._consecutiveFailures += 1;
        const { status } = error as { status?: unknown };
        const repeatedRejection = this._consecutiveFailures >= 2 && REJECTED_REQUEST_STATUSES.includes(status as number);
        const notice = repeatedRejection ? `${errorMessage} ${STUCK_CONVERSATION_NOTICE}` : errorMessage;
        reportIncomplete(notice);
        return notice;
      }
      return llmResponse;
    } finally {
      this._activeAttachmentEnvelope = null;
      this._currentInvokeCallbacks.clear();
      if (this._clearPending) {
        this._clearPending = false;
        this._performDeferredClear();
      }
    }
  };

  /** The order whose screen is open; readScreen only reports it while the URL is still on that order. */
  setActiveOrder = (order: ActiveOrder) => {
    this.activeOrder = order;
  };

  setCurrentTools = (toolFunctions: ToolFunction[]) => {
    this.currentTools = toolFunctions;
  };

  addCurrentTool = (toolFunction: ToolFunction) => {
    this.currentTools.push(toolFunction);
  };

  setCurrentOrderNarrative = (narrative: string) => {
    this.currentOrderNarrative = narrative;
  };

  public setUserInfo = (data: string) => { this.userInfo = data; };

  private _performDeferredClear = (): void => {
    this._hasConversation = false;
    this._consecutiveFailures = 0;
    this.checkpointer = new MemorySaver();
    this.config = { configurable: { thread_id: `session-${uuidv4()}` } };
    this.rebuildAgent();
  };

  /** True while the thread holds messages, so sending a new import must clear it first. */
  public hasConversation = (): boolean => this._hasConversation;

  /** True while an answer is still being produced; a clear requested now is deferred, not applied. */
  public isBusy = (): boolean => this._currentInvokeCallbacks.size > 0;

  public clearHistory = async (): Promise<void> => {
    if (this._currentInvokeCallbacks.size > 0) {
      this._clearPending = true;
      return;
    }
    this._performDeferredClear();
  };

  public getModelInference = () => {
    return this.modelNameSelected;
  };

  /**
	 * Tool-less, single-turn model call for extraction. Uses no MCP/write tools,
	 * no agent, no checkpointer (sidebar conversation), and no routing prefix.
	 * Errors propagate to the caller.
	 */
  public extractText = async (prompt: string, signal?: AbortSignal): Promise<string> => {
    return this.invokeExtraction([new HumanMessage(prompt)], signal);
  };

  public extractFromImage = async (prompt: string, imageDataUrl: string, signal?: AbortSignal): Promise<string> => {
    const content = [
      { type: 'text', text: prompt },
      { type: 'image_url', image_url: { url: imageDataUrl } },
    ];
    return this.invokeExtraction([new HumanMessage({ content })], signal);
  };

  private invokeExtraction = async (messages: BaseMessage[], signal?: AbortSignal): Promise<string> => {
    const token = await getValidAccessToken();
    const model = this.buildModel(token, this.modelNameSelected, EXTRACTION_TEMPERATURE, EXTRACTION_MAX_TOKENS, 'extraction');
    const response = await model.invoke(messages, signal ? { signal } : undefined);
    if (readFinishReason(response.response_metadata) === 'length') {
      throw new Error('The model output was cut off (token limit). Try again.');
    }
    return contentToText(response.content);
  };
}
